import { Injectable } from '@angular/core';

import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';

import {
  BehaviorSubject,
  Subject,
  Subscription,
  filter,
  first,
  firstValueFrom,
} from 'rxjs';

import { Store } from '@ngrx/store';
import { selectUser } from 'src/store/user/user.state';

import { Logger } from './logger.service';
import { Utility } from './utility.service';

import { environment } from 'src/environments/environment';

import { EnSignalRNotificationTypes } from 'src/enums/EnSignalRNotificationTypes';

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private hubConnection: HubConnection;

  public get connectionId(): string | null {
    return this.hubConnection.connectionId;
  }

  public isConnected$ = new BehaviorSubject<boolean>(false);
  public joinedClientPool$ = new BehaviorSubject<boolean>(false);

  private userSubscription?: Subscription;

  private readonly automaticReconnectDelayInMs: number = 5000;

  private readonly notificationTypes: EnSignalRNotificationTypes[] = [
    EnSignalRNotificationTypes.CloudBackupClientOnline,
    EnSignalRNotificationTypes.CloudBackupClientOffline,
    // EnSignalRNotificationTypes.CloudBackupUploadingFileStart,
    // EnSignalRNotificationTypes.CloudBackupUploadingFileError,
    // EnSignalRNotificationTypes.CloudBackupUploadingFileCompleted,
    EnSignalRNotificationTypes.CloudBackupClientLogFileReceived,
    EnSignalRNotificationTypes.CloudBackupMultiFileDownloadReady,
    EnSignalRNotificationTypes.CloudBackupMultiFileDownloadError,
    EnSignalRNotificationTypes.CloudBackupSingleFileDownloadUrlReceived,
    EnSignalRNotificationTypes.CashSystemDataFetched,
    EnSignalRNotificationTypes.CashSystemRevenuesForDaysFetched,
    EnSignalRNotificationTypes.CashSystemPossibleReportsFetched,
    EnSignalRNotificationTypes.CashSystemReportFetched,
    EnSignalRNotificationTypes.CashSystemOnline,
    EnSignalRNotificationTypes.CashSystemOffline,
    EnSignalRNotificationTypes.PmsSystemDataFetched,
    EnSignalRNotificationTypes.PmsSystemRevenuesForDaysFetched,
    EnSignalRNotificationTypes.PmsSystemOnline,
    EnSignalRNotificationTypes.PmsSystemOffline,
    EnSignalRNotificationTypes.TableReservationCreated,
    EnSignalRNotificationTypes.TableReservationEdited,
    EnSignalRNotificationTypes.PmsSystemRoomReservationSearchResultsReceived,
    EnSignalRNotificationTypes.PmsSystemRoomReservationInfoFetched,
    EnSignalRNotificationTypes.PmsSystemRoomReservationInfoFetchedBySourceId,
    EnSignalRNotificationTypes.ReportDesignerReportSaved,
    EnSignalRNotificationTypes.AccountingMonthlyReportDownloadNoData,
    EnSignalRNotificationTypes.AccountingMonthlyReportDownloadProgress,
    EnSignalRNotificationTypes.AccountingMonthlyReportDownloadFailed,
    EnSignalRNotificationTypes.AccountingMonthlyReportDownloadReady,
  ];

  public notificationReceived$ = new Subject<SignalRNotification>();

  constructor(private store: Store) {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(environment.signalRUrl)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          Logger.logInfo(
            'Attempting to reconnect to Legacy SignalR via IRetryPolicy...',
          );

          return this.automaticReconnectDelayInMs;
        },
      })
      .configureLogging(LogLevel.Error)
      .build();

    this.hubConnection.onreconnecting((error) => {
      this.isConnected$.next(false);
      this.joinedClientPool$.next(false);
      Logger.logInfo('Starting Legacy SignalR reconnection process...');
    });

    this.hubConnection.onreconnected((connectionId) => {
      this.isConnected$.next(true);
      Logger.logInfo('Legacy SignalR reconnected!');

      if (Utility.authToken) {
        this.joinClientPool();
      }
    });

    this.hubConnection.onclose((error) => {
      this.isConnected$.next(false);
      this.joinedClientPool$.next(false);
      Logger.logInfo('Legacy SignalR disconnected!');

      setTimeout(() => {
        Logger.logInfo('Attempting to reconnect to Legacy SignalR...');
        this.startConnection();
      }, this.automaticReconnectDelayInMs);
    });

    this.hubConnection.on(
      'SendNotification',
      (notificationType: EnSignalRNotificationTypes, message?: any) => {
        this.notificationReceived$.next({
          type: notificationType,
          message: message ? Utility.ungzip(message) : undefined,
        });
      },
    );

    this.hubConnection.on('JoinClientPoolResponse', () => {
      this.joinedClientPool$.next(true);
      Logger.logInfo('Legacy SignalR joined client pool!');
    });

    this.hubConnection.on('LeaveClientPoolResponse', () => {
      this.joinedClientPool$.next(false);
      Logger.logInfo('Legacy SignalR left client pool!');
    });
  }

  public startConnection(): void {
    this.hubConnection
      .start()
      .then(async () => {
        this.isConnected$.next(true);
        Logger.logInfo('Legacy SignalR connected!');

        await firstValueFrom(
          this.store.select(selectUser).pipe(
            filter((user) => user !== undefined),
            first(),
          ),
        );

        this.setupUserSubscription();
      })
      .catch((error: Error) => {
        console.error(error);

        setTimeout(() => {
          Logger.logInfo('Attempting to reconnect to Legacy SignalR...');
          this.startConnection();
        }, this.automaticReconnectDelayInMs);
      });
  }

  private setupUserSubscription(): void {
    this.userSubscription = this.store
      .select(selectUser)
      .subscribe(async (user) => {
        const joinedClientPool: boolean = await firstValueFrom(
          this.joinedClientPool$,
        );

        if (user && !joinedClientPool) {
          this.joinClientPool();
        } else if (!user && joinedClientPool) {
          this.leaveClientPool();
        }
      });
  }

  public closeConnection(): void {
    if (!this.connectionId) {
      Logger.logError(`ERROR SignalR: Cannot close non existing connection.`);
      return;
    }

    this.hubConnection
      .stop()
      .then(() => {
        this.isConnected$.next(false);
        this.joinedClientPool$.next(false);
        Logger.logInfo('Legacy SignalR disconnected!');

        if (this.userSubscription) {
          this.userSubscription.unsubscribe();
        }
      })
      .catch((error: Error) => {
        console.error(error);
      });
  }

  private async joinClientPool(): Promise<void> {
    if (await firstValueFrom(this.joinedClientPool$)) {
      Logger.logError(`ERROR Legacy SignalR: Already joined client pool.`);
      return;
    }

    if (!Utility.authToken) {
      Logger.logError(
        `ERROR SignalR: Cannot join client pool without being logged in.`,
      );
      return;
    }

    if (!(await firstValueFrom(this.isConnected$))) {
      Logger.logError(
        `ERROR SignalR: Cannot join client pool without being connected to SignalR.`,
      );
      return;
    }

    this.hubConnection.send(
      'JoinClientPoolRequest',
      Utility.authToken,
      this.notificationTypes,
    );
  }

  private async leaveClientPool(): Promise<void> {
    if (!(await firstValueFrom(this.joinedClientPool$))) {
      Logger.logError(
        `ERROR SignalR: Cannot leave client pool without having joined.`,
      );
      return;
    }

    if (!(await firstValueFrom(this.isConnected$))) {
      Logger.logError(
        `ERROR SignalR: Cannot leave client pool without being connected to SignalR.`,
      );
      this.joinedClientPool$.next(false);
      return;
    }

    this.hubConnection.send('LeaveClientPoolRequest', this.connectionId);
  }
}

export class SignalRNotification {
  public type!: EnSignalRNotificationTypes;
  public message?: any;
}
